import {debounce} from 'lodash';
import { Controller } from "@hotwired/stimulus";

/**
 * Mixin for detecting browser zoom level changes
 * for controllers that need to respond to zoom changes.
 * Note that you can use this mixin only for Stimulus controllers.
 *
 * Usage:
 * import {useZoomListener} from "./mixins/use_zoom_listener.js";
 *
 * export default class extends Controller {
 *   connect() {
 *     useZoomListener(this);
 *   }
 *
 *   onZoomChange() {
 *     // callback function
 *   }
 * }
 */
export function useZoomListener(controller, options = {}) {
  const {
    debounceDelay = 100,
    callback = 'onZoomChange'
  } = options;

  if (!(controller instanceof Controller)) {
    console.error('useZoomListener: param controller is not instance of Stimulus Controller');
    throw new Error("useZoomListener: param controller is not instance of Stimulus Controller");
  }

  if (typeof controller[callback] !== 'function') {
    console.error(`useZoomListener: controller.${callback} method is required`);
    throw new Error(`useZoomListener: controller.${callback} method is required`);
  }

  controller.lastZoom = _getZoomLevel();

  const debouncedCheckZoomChange = debounce(() => {
    _checkZoomChange(controller, callback);
  }, debounceDelay);

  controller._zoomListener = debouncedCheckZoomChange;

  window.addEventListener('resize', controller._zoomListener);
  if (window.visualViewport) {
    window.visualViewport.addEventListener('resize', controller._zoomListener);
    window.visualViewport.addEventListener('scroll', controller._zoomListener);
  }

  const originalDisconnect = controller.disconnect.bind(controller);

  controller.disconnect = function() {
    window.removeEventListener('resize', controller._zoomListener);
    if (window.visualViewport) {
      window.visualViewport.removeEventListener('resize', controller._zoomListener);
      window.visualViewport.removeEventListener('scroll', controller._zoomListener);
    }
    originalDisconnect();
  };
}

function _getZoomLevel() {
  let zoom = window.devicePixelRatio || 1;
  if (window.visualViewport && window.visualViewport.scale && window.visualViewport.scale !== 1) {
    zoom = window.visualViewport.scale;
  } else if (!window.devicePixelRatio) {
    zoom = window.outerWidth / window.innerWidth;
  }
  return Math.round(zoom * 100) / 100;
}

function _checkZoomChange(controller, callback) {
  const currentZoom = _getZoomLevel();
  if (currentZoom !== controller.lastZoom) {
    controller[callback]();
    controller.lastZoom = currentZoom;
  }
}